// src/components/dialog/index.tsx
//
// Uso:
//   const dialog = useDialog();
//
//   // Loading
//   const id = dialog.loading("Buscando dados...");
//   dialog.close(id);
//
//   // Sucesso
//   dialog.success("Técnico removido!", "O vínculo foi desfeito.");
//
//   // Erro
//   dialog.error("Algo deu errado", retorno.mensagem);
//
//   // Confirmação (retorna Promise<boolean>)
//   const ok = await dialog.confirm({
//     title: "Remover técnico?",
//     description: "O vínculo será removido. O cadastro não será excluído.",
//     confirmLabel: "Sim, remover",
//     danger: true,
//   });
//   if (!ok) return;

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import styles from "./styles.module.css";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaSpinner,
  FaTimes,
} from "react-icons/fa";

// ─── Tipos ───────────────────────────────────────────────────────────────────

type DialogType = "loading" | "success" | "error" | "confirm";

interface DialogItem {
  id: string;
  type: DialogType;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  autoClose?: number; // ms
  resolve?: (value: boolean) => void;
}

interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

interface DialogContextType {
  loading: (title: string) => string;
  success: (title: string, description?: string, autoClose?: number) => string;
  error: (title: string, description?: string) => string;
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  close: (id: string) => void;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const DialogContext = createContext<DialogContextType | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

export function DialogProvider({ children }: { children: ReactNode }) {
  const [dialogs, setDialogs] = useState<DialogItem[]>([]);
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const genId = () => Math.random().toString(36).slice(2);

  const add = useCallback((item: Omit<DialogItem, "id">) => {
    const id = genId();
    setDialogs((prev) => [...prev, { ...item, id }]);

    if (item.autoClose) {
      timers.current[id] = setTimeout(() => remove(id), item.autoClose);
    }

    return id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = useCallback((id: string) => {
    clearTimeout(timers.current[id]);
    delete timers.current[id];
    setDialogs((prev) => prev.filter((d) => d.id !== id));
  }, []);

  const loading = useCallback(
    (title: string) => add({ type: "loading", title }),
    [add],
  );

  const success = useCallback(
    (title: string, description?: string, autoClose = 2200) =>
      add({ type: "success", title, description, autoClose }),
    [add],
  );

  const error = useCallback(
    (title: string, description?: string) =>
      add({ type: "error", title, description }),
    [add],
  );

  const confirm = useCallback(
    (options: ConfirmOptions): Promise<boolean> =>
      new Promise((resolve) => {
        add({ type: "confirm", ...options, resolve });
      }),
    [add],
  );

  const close = useCallback((id: string) => remove(id), [remove]);

  const handleConfirm = (dialog: DialogItem) => {
    dialog.resolve?.(true);
    remove(dialog.id);
  };

  const handleCancel = (dialog: DialogItem) => {
    dialog.resolve?.(false);
    remove(dialog.id);
  };

  return (
    <DialogContext.Provider value={{ loading, success, error, confirm, close }}>
      {children}

      {dialogs.length > 0 && (
        <div className={styles.overlay}>
          {dialogs.map((dialog) => (
            <div
              key={dialog.id}
              className={styles.dialog}
              role="dialog"
              aria-modal="true"
            >
              {/* Ícone */}
              <div className={`${styles.iconWrap} ${styles[dialog.type]}`}>
                {dialog.type === "loading" && (
                  <FaSpinner className={styles.spin} />
                )}
                {dialog.type === "success" && <FaCheckCircle />}
                {dialog.type === "error" && <FaTimesCircle />}
                {dialog.type === "confirm" && <FaExclamationTriangle />}
              </div>

              {/* Fechar (exceto loading) */}
              {dialog.type !== "loading" && (
                <button
                  className={styles.closeBtn}
                  onClick={() => handleCancel(dialog)}
                  aria-label="Fechar"
                >
                  <FaTimes />
                </button>
              )}

              {/* Conteúdo */}
              <h3 className={styles.title}>{dialog.title}</h3>
              {dialog.description && (
                <p className={styles.description}>{dialog.description}</p>
              )}

              {/* Barra de progresso para auto-close */}
              {dialog.autoClose && dialog.type !== "loading" && (
                <div className={styles.progressWrap}>
                  <div
                    className={styles.progressBar}
                    style={{
                      animationDuration: `${dialog.autoClose}ms`,
                    }}
                  />
                </div>
              )}

              {/* Ações */}
              {dialog.type === "confirm" && (
                <div className={styles.actions}>
                  <button
                    className={styles.cancelBtn}
                    onClick={() => handleCancel(dialog)}
                  >
                    {dialog.cancelLabel ?? "Cancelar"}
                  </button>
                  <button
                    className={`${styles.confirmBtn} ${
                      dialog.danger ? styles.danger : ""
                    }`}
                    onClick={() => handleConfirm(dialog)}
                  >
                    {dialog.confirmLabel ?? "Confirmar"}
                  </button>
                </div>
              )}

              {dialog.type === "error" && (
                <div className={styles.actions}>
                  <button
                    className={styles.confirmBtn}
                    onClick={() => handleCancel(dialog)}
                  >
                    Fechar
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </DialogContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useDialog() {
  const ctx = useContext(DialogContext);
  if (!ctx)
    throw new Error("useDialog deve ser usado dentro de <DialogProvider>");
  return ctx;
}
